//React
import React, { useId, useMemo } from "react";
import PropTypes from "prop-types";
import { Chart } from "react-chartjs-2";
import { cssToken } from "../../utilities/cssToken";
import {
	registerCharts,
	chartTheme,
	baseOptions,
	legendPlugin,
	formatValue,
	valueLabel,
	NUMERIC_TYPES,
} from "./chartTheme";
import { assignSeriesColors, categoricalToken } from "./palette";
//CSS
import "./charts.scss";

/**
 * A `bar` chart across categories (ASB-4377): one bar per row. The manifest's
 * `x`/`y` name the category and value columns when set; otherwise the first
 * non-numeric column is the category and every numeric column is a value
 * series (grouped bars, one categorical colour per column). With one value
 * series and a role-mapped category, each bar takes its value's role colour.
 */
export function breakdownColumns(dataset) {
	const columns = dataset.columns ?? [];
	const category =
		(dataset.x && columns.find((c) => c.key === dataset.x)) ||
		columns.find((c) => !NUMERIC_TYPES.includes(c.type));
	const values = dataset.y
		? columns.filter((c) => c.key === dataset.y)
		: columns.filter((c) => NUMERIC_TYPES.includes(c.type));
	return { category, values };
}

function BreakdownBar({ dataset, rows, ariaLabel }) {
	registerCharts();
	const legendId = `as-chart-legend-${useId().replace(/:/g, "")}`;
	const theme = useMemo(chartTheme, []);
	const { category, values } = useMemo(() => breakdownColumns(dataset), [dataset]);
	const data = rows ?? [];

	if (!category || values.length === 0) {
		return (
			<p className="as-chart__message">This dataset has no category and value columns to chart.</p>
		);
	}

	const single = values.length === 1;
	const byValue =
		single && category.roles
			? assignSeriesColors(
					data.map((r) => r[category.key]),
					category.roles,
					cssToken
			  )
			: null;
	const chartData = {
		labels: data.map((r) => valueLabel(category, r[category.key], "axis")),
		datasets: values.map((col, i) => ({
			type: "bar",
			label: col.label,
			data: data.map((r) => {
				const n = Number(r[col.key]);
				return Number.isFinite(n) ? n : null;
			}),
			backgroundColor: byValue
				? data.map((r) => byValue.get(String(r[category.key] ?? ""))?.color)
				: cssToken(categoricalToken(i)),
			maxBarThickness: 36,
			borderRadius: 2,
			borderSkipped: false,
		})),
	};
	const options = baseOptions(theme, {
		legendId,
		yTitle: single ? values[0].label : undefined,
		yTick: (value) => formatValue(values[0].type, value, "axis"),
		tooltipTitle: (items) =>
			items.length ? valueLabel(category, data[items[0].dataIndex]?.[category.key], "cell") : "",
		tooltipLabel: (ctx) =>
			`${ctx.dataset.label}: ${formatValue(values[ctx.datasetIndex]?.type, ctx.parsed.y, "cell")}`,
	});

	return (
		<div className="as-chart as-chart--breakdown">
			<div className="as-chart__body">
				<Chart
					type="bar"
					data={chartData}
					options={options}
					plugins={[legendPlugin]}
					role="img"
					aria-label={ariaLabel ?? `${dataset.title ?? values[0].label} by ${category.label}`}
				/>
			</div>
			<div
				id={legendId}
				className={["as-chart-legend", single ? "as-chart-legend--hidden" : ""].join(" ").trim()}
			/>
		</div>
	);
}

BreakdownBar.propTypes = {
	/** a manifest dataset (design A.2) with chart "bar" */
	dataset: PropTypes.shape({
		id: PropTypes.string,
		title: PropTypes.string,
		chart: PropTypes.oneOf(["bar"]).isRequired,
		x: PropTypes.string,
		y: PropTypes.string,
		columns: PropTypes.array,
	}).isRequired,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	ariaLabel: PropTypes.string,
};

export default BreakdownBar;
